import { useEffect, useRef, useState } from 'react';
import emailjs from '@emailjs/browser';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';


function EnquiryModal({ isOpen, onClose }) {
  const modalRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    countryCode: "",
    message: ""
  });
  const [defaultCountry, setDefaultCountry] = useState("ae");
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ type: "", message: "" });
  const [focusedField, setFocusedField] = useState(null);


  // Auto-detect country from IP on component mount
  useEffect(() => {
    const detectCountry = async () => {
      try {
        const response = await fetch('https://api.country.is');
        const data = await response.json();
        if (data.country) {
          setDefaultCountry(data.country.toLowerCase());
        }
      } catch (error) {
        console.error('Failed to detect country:', error);
        // Keep default as UAE if detection fails
      }
    };


    detectCountry();
  }, []);


  useEffect(() => {
    const modalElement = modalRef.current;
    if (!modalElement) return;


    if (isOpen) {
      modalElement.showModal();
      // Reset form and status when modal opens
      setFormData({
        name: "",
        email: "",
        phone: "",
        countryCode: "",
        message: ""
      });
      setStatus({ type: "", message: "" });
    } else {
      modalElement.close();
    }
  }, [isOpen]);


  // Handle input changes
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };


  // Handle phone number changes
  const handlePhoneChange = (value, country) => {
    setFormData({
      ...formData,
      phone: value,
      countryCode: `+${country.dialCode}`
    });
  };


  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ type: "", message: "" });


    // EmailJS configuration
    const serviceID = "service_dir1nrq";
    const templateID = "template_ikc647q";
    const publicKey = "83nvT9Xv6Y9ty3K5j";


    // Template parameters with separate country code
    const templateParams = {
      from_name: formData.name,
      from_email: formData.email,
      country_code: formData.countryCode,
      phone: formData.phone,
      full_phone: `${formData.countryCode} ${formData.phone}`,
      message: formData.message || "No additional message",
      to_name: "BNW Developments"
    };


    console.log('Form submitted:', templateParams);


    // Send email using EmailJS
    emailjs.send(serviceID, templateID, templateParams, publicKey)
      .then((response) => {
        console.log('SUCCESS!', response.status, response.text);
        setStatus({
          type: "success",
          message: "Thank you! Your enquiry has been submitted successfully. We'll get back to you soon."
        });

        setLoading(false);

        // Close modal after 2 seconds on success
        setTimeout(() => {
          onClose();
        }, 2000);
      })
      .catch((error) => {
        console.error('FAILED...', error);
        setStatus({
          type: "error",
          message: "Oops! Something went wrong. Please try again or contact us directly."
        });
        setLoading(false);
      });
  };


  const handleBackdropClick = (e) => {
    if (e.target === modalRef.current) {
      onClose();
    }
  };

  const inputClasses = (fieldName) => `
    w-full bg-[#f9f9f9] text-gray-900 px-4 py-3 rounded-lg border 
    transition-all duration-300 outline-none placeholder:text-gray-400
    ${focusedField === fieldName
      ? 'border-[#c8a45e] shadow-[0_0_10px_rgba(200,164,94,0.15)] bg-white'
      : 'border-gray-200 hover:border-gray-300 hover:bg-[#f0f0f0]'
    }
  `;


  return (
    <dialog
      ref={modalRef}
      className="backdrop:bg-black/40 backdrop:backdrop-blur-sm bg-transparent p-0 border-none outline-none w-full max-w-lg m-auto open:animate-fade-in-up"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-2xl shadow-2xl overflow-hidden relative w-full">
        <button
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-900 hover:rotate-90 transition-all duration-300 text-2xl w-8 h-8 flex items-center justify-center z-10"
          onClick={onClose}
          type="button"
          aria-label="Close modal"
        >
          ×
        </button>

        <div className="p-8">
          <h2 className="text-3xl font-display font-bold text-gray-900 mb-2 tracking-wide text-center">Get in Touch</h2>
          <p className="text-gray-500 text-center mb-8 text-sm">Fill out the form below and our team will get back to you shortly.</p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-1">
              <label htmlFor="name" className="text-xs font-bold text-[#c8a45e] uppercase tracking-wider ml-1">Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                onFocus={() => setFocusedField('name')}
                onBlur={() => setFocusedField(null)}
                required
                placeholder="Enter your name"
                className={inputClasses('name')}
              />
            </div>


            <div className="space-y-1">
              <label htmlFor="email" className="text-xs font-bold text-[#c8a45e] uppercase tracking-wider ml-1">Email *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                onFocus={() => setFocusedField('email')}
                onBlur={() => setFocusedField(null)}
                required
                placeholder="Enter your email"
                className={inputClasses('email')}
              />
            </div>


            <div className="space-y-1">
              <label htmlFor="phone" className="text-xs font-bold text-[#c8a45e] uppercase tracking-wider ml-1">Phone *</label>
              <div onFocus={() => setFocusedField('phone')} onBlur={() => setFocusedField(null)} className={`transition-all duration-300 rounded-lg ${focusedField === 'phone' ? 'shadow-[0_0_10px_rgba(200,164,94,0.15)]' : ''}`}>
                <PhoneInput
                  country={defaultCountry}
                  value={formData.phone}
                  onChange={handlePhoneChange}
                  inputProps={{
                    name: 'phone',
                    required: true,
                    id: 'phone'
                  }}
                  enableSearch={true}
                  searchPlaceholder="Search country"
                  containerClass="!w-full"
                  inputClass={`!w-full !h-[50px] !bg-[#f9f9f9] !text-gray-900 !pl-[52px] !pr-4 !py-3 !rounded-lg !border !transition-all !duration-300 !outline-none ${focusedField === 'phone' ? '!border-[#c8a45e] !bg-white' : '!border-gray-200 hover:!border-gray-300 hover:!bg-[#f0f0f0]'}`}
                  buttonClass={`!bg-[#f9f9f9] !border-gray-200 !rounded-l-lg hover:!bg-[#f0f0f0] ${focusedField === 'phone' ? '!border-[#c8a45e] !bg-white' : ''}`}
                  dropdownClass="!bg-white !text-gray-900 !border-gray-200 !shadow-xl !rounded-lg scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent"
                  preferredCountries={['ae', 'in', 'sa', 'gb', 'us']}
                  autoFormat={true}
                />
              </div>
            </div>


            <div className="space-y-1">
              <label htmlFor="message" className="text-xs font-bold text-[#c8a45e] uppercase tracking-wider ml-1">Message</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                onFocus={() => setFocusedField('message')}
                onBlur={() => setFocusedField(null)}
                rows="4"
                placeholder="Tell us about your requirements"
                className={inputClasses('message')}
              ></textarea>
            </div>


            <button
              type="submit"
              className="w-full bg-[#c8a45e] text-white font-display font-bold uppercase tracking-wider py-4 rounded-lg 
              hover:bg-[#b08d4b] hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0
              transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed mt-4 shadow-md"
              disabled={loading}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Sending...
                </span>
              ) : "Submit Enquiry"}
            </button>


            {/* Status Messages */}
            {status.message && (
              <div className={`mt-4 p-3 rounded-lg text-sm font-medium text-center animate-fade-in ${status.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                {status.message}
              </div>
            )}
          </form>
        </div>
      </div>
    </dialog>
  );
}


export default EnquiryModal;